import { Box, Button, Typography, useTheme } from "@mui/material";
import { useId, useMemo } from "react";
import { useReminderTypesQuery } from "web_myplann/Api/Reminders/useReminderTypesQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderCreateFields } from "web_myplann/Reminders/Components/ReminderCreateFields";
import { getReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import { useReminderCreateForm } from "web_myplann/Reminders/Hooks/useReminderCreateForm";
import type {
	Reminder,
	ReminderEntity,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { getSelectableTemplates } from "web_myplann/Reminders/Utils/getSelectableTemplates";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { useSheetBottomSpacing } from "web_myplann/Shared/Hooks/useSheetBottomSpacing";
import { findSameDayReminder } from "web_myplann/Shared/Utils/findSameDayReminder";

type Props = {
	entity: ReminderEntity;
	existingReminders: Reminder[];
	validTo?: Date | null;
	open: boolean;
	onClose: () => void;
	onOpenExisting: (reminder: Reminder) => void;
};

export const ReminderCreateDrawer = ({
	entity,
	existingReminders,
	validTo = entity.validTo,
	open,
	onClose,
	onOpenExisting,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const bottomSpacing = useSheetBottomSpacing(theme.spacingTokens.lg);
	const duplicateAlertId = useId();

	const entityConfig = getReminderEntityConfig(entity.type);
	const { data: templates, isPending: isLoadingTemplates } =
		useReminderTypesQuery(entity);

	const selectableTemplates = useMemo(
		() => getSelectableTemplates(templates ?? [], entityConfig, validTo),
		[templates, entityConfig, validTo],
	);

	const { form, submit, isSubmitting } = useReminderCreateForm({
		entity,
		templates: selectableTemplates,
		validTo,
		onSuccess: onClose,
	});

	const date = form.watch("date");
	const duplicate =
		date instanceof Date ? findSameDayReminder(existingReminders, date) : null;

	const hasNothingToAdd =
		!isLoadingTemplates &&
		!entityConfig.allowCustom &&
		selectableTemplates.length === 0;

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Reminders.Form.addTitle)}
			grabber
			paperSx={{ maxHeight: "100%" }}
		>
			{hasNothingToAdd ? (
				<Typography
					variant="body2"
					px={theme.spacingTokens.lg}
					py={theme.spacingTokens.lg}
				>
					{t(Resources.Reminders.Form.noTemplatesHint)}
				</Typography>
			) : (
				<ReminderCreateFields
					form={form}
					templates={selectableTemplates}
					entityConfig={entityConfig}
					validTo={validTo}
					duplicate={duplicate}
					onOpenDuplicate={onOpenExisting}
					duplicateAlertId={duplicateAlertId}
				/>
			)}

			<Box
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				pb={bottomSpacing}
			>
				<Button
					variant="primary"
					onClick={submit}
					aria-describedby={duplicate ? duplicateAlertId : undefined}
					disabled={!form.formState.isValid || hasNothingToAdd}
					loading={isSubmitting}
				>
					{t(Resources.Reminders.Form.addCta)}
				</Button>
			</Box>
		</MyPlannDrawer>
	);
};
